import _ from 'lodash';
import React, { useMemo } from 'react';
import cn from 'classnames';
import { useParams } from 'react-router';

import { Footer, PubnubModule, Header, Room, LogIn, RoomContent } from 'client/components/common';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';
import { selectIsLoggedIn, selectUserRoomRole, selectUserUID } from 'client/redux/client/selectors';
import JoinToSpacePopup from 'client/components/common/Popups/JoinToSpacePopup';
import { selectListenersAllowedToSpeak } from 'client/redux/pubnub/selectors';
import { useGAEventCommonProps } from 'client/hooks/ga-event-common-props';
import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import { selectIsShowJoinToSpacePopup, selectShouldInitPubnub } from 'client/redux/ui/selectors';
import { selectRoomStatus } from 'client/redux/room/selectors';
import { getLoginFormType } from 'client/redux/login/selectors';
import { ROOM_STATUS, USER_ROLES } from 'constants/index';
import { analytics } from 'client/utils/analytics';
import { useAppSelector } from 'client/hooks';

import css from './Main.module.scss';

interface Props {
  className?: string;
}

const Main: React.FC<Props> = (props) => {
  const { className = '' } = props;

  const { state } = useLiveStreamContext();
  const params = useParams<{ roomId: string }>();
  const { common } = useGAEventCommonProps();

  const listOfListenersAllowedToSpeak = useAppSelector(selectListenersAllowedToSpeak);
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const shouldInitPubnub = useAppSelector(selectShouldInitPubnub);
  const userLoginType = useAppSelector(getLoginFormType);
  const isJoinSpaceModalVisible = useAppSelector(selectIsShowJoinToSpacePopup);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, params.roomId));
  const userId = useAppSelector(selectUserUID);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const roomStatus = useAppSelector(selectRoomStatus);

  const gaIntervalIDRef = React.useRef<NodeJS.Timeout | null>(null);

  const isRoomFinished = roomStatus === ROOM_STATUS.FINISHED;

  const canPublishAudio = useMemo(() => {
    if (userRole !== USER_ROLES.LISTENER) return userRole !== USER_ROLES.ADMIN;
    return _.findIndex(listOfListenersAllowedToSpeak, { id: userId }) >= 0;
  }, [listOfListenersAllowedToSpeak, userId, userRole]);

  React.useEffect(() => {
    analytics.gtag.event('audio_chat_user_interaction', {
      ...common,
      user_joined: Date.now(),
    });
  }, []);

  React.useEffect(() => {
    if (!isBroadcasting) {
      clearInterval(gaIntervalIDRef?.current ?? undefined);
    }

    if (isBroadcasting) {
      gaIntervalIDRef.current = setInterval(() => {
        analytics.gtag.event('audio_chat_time_interval', {
          ...common,
          chat_time_interval: Date.now(),
        });
      }, 1000 * 60);
    }
  }, [isBroadcasting]);

  return (
    <div className={cn(css.main, className)}>
      <Header roomId={params.roomId} />
      {shouldInitPubnub && <PubnubModule roomId={params.roomId} />}

      <RoomContent />

      {state.space && <Room />}

      <LogIn userLoginType={userLoginType} />
      {shouldInitPubnub && <Footer roomId={params.roomId} />}

      <JoinToSpacePopup
        userRole={userRole}
        canPublishAudio={canPublishAudio}
        visible={
          (!isRoomFinished && !state.localParticipant && isBroadcasting && isLoggedIn) || // for all users who is loggedIn and broadcasting is started
          isJoinSpaceModalVisible || // manual change this variable in some actions
          state.startAudioIssue // when we need user interaction
        }
      />
    </div>
  );
};

export default Main;
